// src/design/PublicLandingPage.jsx
// Public-facing landing page renderer — no auth required.
// Fetches landing page data by slug, renders the chosen layout,
// fires analytics on load and CTA click.

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

// Generate a simple session ID for analytics dedup
function getSessionId() {
  let sid = sessionStorage.getItem('eh_lp_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('eh_lp_sid', sid);
  }
  return sid;
}

// Detect referrer source from URL params or document.referrer
function detectReferrer() {
  const params = new URLSearchParams(window.location.search);
  const utm = (params.get('utm_source') || '').toLowerCase();
  if (utm) {
    if (utm.includes('facebook') || utm.includes('fb')) return 'facebook';
    if (utm.includes('instagram') || utm.includes('ig')) return 'instagram';
    if (utm.includes('linkedin')) return 'linkedin';
    if (utm.includes('twitter') || utm.includes('x.com')) return 'twitter';
    if (utm.includes('tiktok')) return 'tiktok';
    return utm;
  }
  const ref = (document.referrer || '').toLowerCase();
  if (ref.includes('facebook.com') || ref.includes('fb.com')) return 'facebook';
  if (ref.includes('instagram.com')) return 'instagram';
  if (ref.includes('linkedin.com')) return 'linkedin';
  if (ref.includes('twitter.com') || ref.includes('x.com')) return 'twitter';
  if (ref.includes('tiktok.com')) return 'tiktok';
  if (ref) return 'other';
  return 'direct';
}

// Fire analytics event
async function trackEvent(slug, eventType) {
  try {
    await fetch(`${API_URL}/api/designcenter/public/landing/${slug}/analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType,
        referrerSource: detectReferrer(),
        sessionId: getSessionId(),
      }),
    });
  } catch (err) {
    // Silent fail — analytics shouldn't break the page
  }
}

// ─── Build asset URL from asset_id ──────────────────────────────────────────
export function assetUrl(assetId) {
  if (!assetId) return null;
  return `${API_URL}/api/designcenter/assets/${assetId}/file`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function PublicLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContact, setShowContact] = useState(false);
  const viewTracked = useRef(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/public/landing/${slug}`);
        const data = await res.json();
        if (data.ok && data.landingPage) {
          setPage(data.landingPage);
          // Track view once
          if (!viewTracked.current) {
            viewTracked.current = true;
            trackEvent(slug, 'view');
          }
        } else {
          setError('Page not found');
        }
      } catch (err) {
        setError('Failed to load page');
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
          <p className="text-gray-500">This landing page doesn't exist or hasn't been published yet.</p>
        </div>
      </div>
    );
  }

  const accentColor = page.accent_color || '#2563EB';
  const heroImageUrl = assetUrl(page.hero_image_id);
  const sections = (page.sections_json || []).map(s => ({
    ...s,
    image_url: assetUrl(s.image_asset_id),
  }));

  const ctaType = page.cta_type || 'contact_us';

  const handleCtaClick = () => {
    trackEvent(slug, 'cta_click');
    if (ctaType === 'contact_us') {
      setShowContact(true);
    } else if (ctaType === 'call_now' && page.cta_url) {
      window.open(page.cta_url.startsWith('tel:') ? page.cta_url : `tel:${page.cta_url}`, '_self');
    } else if (page.cta_url) {
      window.open(page.cta_url.startsWith('http') ? page.cta_url : `https://${page.cta_url}`, '_blank', 'noopener');
    }
  };

  const bizInfo = {
    name: page.business_name || '',
    address: page.business_address || '',
    city: page.business_city || '',
    state: page.business_state || '',
    zip: page.business_zip || '',
    phone: page.business_phone || '',
    website: page.business_website || '',
    email: page.user_email || '',
  };

  const props = {
    heroImageUrl,
    heroAssetType: page.hero_asset_type || 'image',
    heroHeadline: page.hero_headline,
    heroSubheadline: page.hero_subheadline,
    sections,
    accentColor,
    ctaText: page.cta_text,
    onCtaClick: handleCtaClick,
    businessName: page.business_name,
  };

  let layout;
  switch (page.layout) {
    case 'grid-gallery':      layout = <GridGalleryPublic {...props} />; break;
    case 'feature-strip':     layout = <FeatureStripPublic {...props} />; break;
    case 'compact-editorial': layout = <CompactEditorialPublic {...props} />; break;
    default:                  layout = <BoldHeroPublic {...props} />;
  }

  return (
    <>
      {layout}
      {showContact && (
        <ContactToast bizInfo={bizInfo} accentColor={accentColor} onClose={() => setShowContact(false)} />
      )}
    </>
  );
}

// ─── Shared hero block ───────────────────────────────────────────────────────
function HeroBlock({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, accentColor, tall }) {
  const isVideo = heroAssetType === 'video';
  if (heroImageUrl) {
    return (
      <div className="relative w-full overflow-hidden" style={{ minHeight: tall ? '400px' : '280px' }}>
        {isVideo ? (
          <video src={heroImageUrl} className="w-full object-cover" style={{ height: tall ? '400px' : '280px' }} autoPlay loop muted playsInline />
        ) : (
          <img src={heroImageUrl} alt="" className="w-full object-cover" style={{ height: tall ? '400px' : '280px' }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-10 max-w-4xl mx-auto">
          <h1 className="text-4xl font-extrabold text-white mb-3" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
            {heroHeadline || ''}
          </h1>
          <p className="text-xl text-white/90" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
            {heroSubheadline || ''}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center py-16 px-10" style={{ background: accentColor }}>
      <h1 className="text-4xl font-extrabold text-white mb-3 text-center">{heroHeadline || ''}</h1>
      <p className="text-xl text-white/80 text-center max-w-2xl">{heroSubheadline || ''}</p>
    </div>
  );
}

// ─── Contact info toast (for contact_us CTA) ────────────────────────────────
export function ContactToast({ bizInfo, accentColor, onClose }) {
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[420px] max-w-[90vw] overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100" style={{ background: `${accentColor}08` }}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">{bizInfo.name || 'Contact Us'}</h3>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3">
          {bizInfo.address && (
            <div className="flex items-start gap-3">
              <svg className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
              </svg>
              <div className="text-sm text-gray-700">
                <div>{bizInfo.address}</div>
                {(bizInfo.city || bizInfo.state || bizInfo.zip) && (
                  <div>{[bizInfo.city, bizInfo.state].filter(Boolean).join(', ')}{bizInfo.zip ? ` ${bizInfo.zip}` : ''}</div>
                )}
              </div>
            </div>
          )}
          {bizInfo.phone && (
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
              </svg>
              <a href={`tel:${bizInfo.phone.replace(/[^+\d]/g, '')}`}
                className="text-sm font-medium hover:underline" style={{ color: accentColor }}>
                {bizInfo.phone}
              </a>
            </div>
          )}
          {bizInfo.email && (
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
              <a href={`mailto:${bizInfo.email}`}
                className="text-sm font-medium hover:underline" style={{ color: accentColor }}>
                {bizInfo.email}
              </a>
            </div>
          )}
          {bizInfo.website && (
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
              <a href={bizInfo.website.startsWith('http') ? bizInfo.website : `https://${bizInfo.website}`}
                target="_blank" rel="noopener noreferrer"
                className="text-sm font-medium hover:underline" style={{ color: accentColor }}>
                {bizInfo.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared CTA block ────────────────────────────────────────────────────────
function CTABlock({ accentColor, ctaText, onCtaClick }) {
  return (
    <div className="py-16 text-center" style={{ background: `${accentColor}10` }}>
      <button
        onClick={onCtaClick}
        className="px-10 py-4 text-white font-bold rounded-xl shadow-lg text-lg hover:opacity-90 transition-opacity cursor-pointer"
        style={{ background: accentColor }}
      >
        {ctaText || 'Contact Us'}
      </button>
    </div>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────
function PageFooter({ businessName }) {
  return (
    <div className="py-6 text-center bg-gray-50 border-t border-gray-200">
      <p className="text-xs text-gray-400">
        {businessName && <span>{businessName} &middot; </span>}
        Powered by <a href="https://ehandshake.com" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">eHandshake</a>
      </p>
    </div>
  );
}

// ━━━ LAYOUT 1: BOLD HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function BoldHeroPublic({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName }) {
  return (
    <div className="min-h-screen bg-white">
      <HeroBlock heroImageUrl={heroImageUrl} heroAssetType={heroAssetType} heroHeadline={heroHeadline} heroSubheadline={heroSubheadline} accentColor={accentColor} tall />
      <div className="max-w-4xl mx-auto py-12 px-6">
        {sections.map((section, i) => (
          <div key={i} className={`flex items-start gap-8 mb-12 ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
            {section.image_url && (
              <div className="flex-shrink-0 w-2/5">
                <img src={section.image_url} alt="" className="w-full rounded-xl shadow-md object-cover" style={{ maxHeight: '250px' }} />
              </div>
            )}
            <div className={section.image_url ? 'flex-1' : 'w-full'}>
              <h2 className="text-2xl font-bold mb-3" style={{ color: accentColor }}>{section.headline}</h2>
              <p className="text-gray-600 leading-relaxed text-base">{section.body}</p>
            </div>
          </div>
        ))}
      </div>
      <CTABlock accentColor={accentColor} ctaText={ctaText} onCtaClick={onCtaClick} />
      <PageFooter businessName={businessName} />
    </div>
  );
}

// ━━━ LAYOUT 2: GRID GALLERY ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function GridGalleryPublic({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <HeroBlock heroImageUrl={heroImageUrl} heroAssetType={heroAssetType} heroHeadline={heroHeadline} heroSubheadline={heroSubheadline} accentColor={accentColor} />
      <div className="max-w-4xl mx-auto py-12 px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sections.map((section, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {section.image_url && (
                <img src={section.image_url} alt="" className="w-full h-48 object-cover" />
              )}
              <div className="p-5">
                <h2 className="text-lg font-bold mb-2" style={{ color: accentColor }}>{section.headline}</h2>
                <p className="text-gray-600 text-sm leading-relaxed">{section.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <CTABlock accentColor={accentColor} ctaText={ctaText} onCtaClick={onCtaClick} />
      <PageFooter businessName={businessName} />
    </div>
  );
}

// ━━━ LAYOUT 3: FEATURE STRIP ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function FeatureStripPublic({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName }) {
  return (
    <div className="min-h-screen bg-white">
      <HeroBlock heroImageUrl={heroImageUrl} heroAssetType={heroAssetType} heroHeadline={heroHeadline} heroSubheadline={heroSubheadline} accentColor={accentColor} tall />
      {sections.map((section, i) => (
        <div key={i} className={`py-12 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <div className={`max-w-5xl mx-auto px-6 flex items-center gap-10 ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
            {section.image_url && (
              <div className="flex-shrink-0 w-2/5">
                <img src={section.image_url} alt="" className="w-full rounded-xl shadow-md object-cover" style={{ maxHeight: '280px' }} />
              </div>
            )}
            <div className={section.image_url ? 'flex-1' : 'w-full max-w-3xl mx-auto'}>
              <h2 className="text-2xl font-bold mb-3" style={{ color: accentColor }}>{section.headline}</h2>
              <p className="text-gray-600 leading-relaxed text-base">{section.body}</p>
            </div>
          </div>
        </div>
      ))}
      <div className="py-16 text-center" style={{ background: accentColor }}>
        <button
          onClick={onCtaClick}
          className="px-10 py-4 bg-white font-bold rounded-xl shadow-lg text-lg hover:opacity-90 transition-opacity cursor-pointer"
          style={{ color: accentColor }}
        >
          {ctaText || 'Contact Us'}
        </button>
      </div>
      <PageFooter businessName={businessName} />
    </div>
  );
}

// ━━━ LAYOUT 4: COMPACT EDITORIAL ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function CompactEditorialPublic({ heroImageUrl, heroAssetType, heroHeadline, heroSubheadline, sections, accentColor, ctaText, onCtaClick, businessName }) {
  const isVideo = heroAssetType === 'video';
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto pt-10 px-6">
        {heroImageUrl && (
          <div className="rounded-xl overflow-hidden shadow-md mb-6">
            {isVideo ? (
              <video src={heroImageUrl} className="w-full object-cover" style={{ maxHeight: '350px' }} autoPlay loop muted playsInline />
            ) : (
              <img src={heroImageUrl} alt="" className="w-full object-cover" style={{ maxHeight: '350px' }} />
            )}
          </div>
        )}
        <h1 className="text-3xl font-extrabold text-gray-900 mb-3">{heroHeadline}</h1>
        <p className="text-lg text-gray-500 mb-8 pb-6 border-b border-gray-200">{heroSubheadline}</p>

        {sections.map((section, i) => (
          <div key={i} className="mb-10">
            <h2 className="text-xl font-bold mb-3" style={{ color: accentColor }}>{section.headline}</h2>
            <p className="text-gray-700 leading-relaxed text-base mb-4">{section.body}</p>
            {section.image_url && (
              <div className="rounded-lg overflow-hidden shadow-sm my-4">
                <img src={section.image_url} alt="" className="w-full object-cover" style={{ maxHeight: '300px' }} />
              </div>
            )}
          </div>
        ))}

        <div className="text-center py-10 border-t border-gray-200">
          <button
            onClick={onCtaClick}
            className="px-8 py-3 text-white font-semibold rounded-lg shadow-md text-base cursor-pointer"
            style={{ background: accentColor }}
          >
            {ctaText || 'Contact Us'}
          </button>
        </div>
      </div>
      <PageFooter businessName={businessName} />
    </div>
  );
}
